import React, { useEffect } from 'react';

import { Helmet } from 'react-helmet-async';

import { StyledReferralTerms } from './styles';
import { TextLinkColor } from '../../../components/atoms/text-link-color';
import { bemClassName } from '../../../utils/bem';
import { SUPPORT_EMAIL } from '../../../utils/constants';
import { canonical, ogImage, ogUrl } from '../../../utils/seo';

const b = bemClassName('referral-terms-page');

const ReferralTermsPage = () => {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  return (
    <StyledReferralTerms>
      <Helmet>
        <title>Referral Program Terms — Active Shape</title>
        <meta
          name="description"
          content="Review the terms and conditions of the Active Shape Referral Program, including eligibility, commission structure, payouts, and program rules."
        />
        {canonical('/referral-terms') && <link rel="canonical" href={canonical('/referral-terms')} />}
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Referral Program Terms — Active Shape" />
        <meta
          property="og:description"
          content="Review the terms and conditions of the Active Shape Referral Program, including eligibility, commission structure, payouts, and program rules."
        />
        {ogImage() && <meta property="og:image" content={ogImage()} />}
        {ogUrl('/referral-terms') && <meta property="og:url" content={ogUrl('/referral-terms')} />}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Referral Program Terms — Active Shape" />
        <meta
          name="twitter:description"
          content="Review the terms and conditions of the Active Shape Referral Program, including eligibility, commission structure, payouts, and program rules."
        />
        {ogImage() && <meta name="twitter:image" content={ogImage()} />}
      </Helmet>
      <div className={b()}>
        <div className={b('card')}>
          <h1 className={b('title')}>Referral Program Terms and Conditions</h1>
          <p className={b('update')}>Last updated: March 12, 2026</p>

          <div className={b('nav-links')}>
            <TextLinkColor to="/terms-of-service">Terms of Service</TextLinkColor>
            <TextLinkColor to="/privacy-policy">Privacy Policy</TextLinkColor>
          </div>

          <p>
            These Referral Program Terms and Conditions (&quot;Referral Terms&quot;) govern your participation in the{' '}
            <strong>ActiveShape Referral Program</strong> (&quot;Program&quot;). By participating, you agree to be bound
            by these Referral Terms, in addition to our general{' '}
            <a href="/terms-of-service">Terms and Conditions of Service</a> and{' '}
            <a href="/privacy-policy">Privacy Policy</a>.
          </p>

          <h2 className={b('section-title')}>1. Program Overview</h2>
          <p>
            The ActiveShape Referral Program allows registered users (&quot;Referrers&quot;) to earn recurring cash
            commissions by referring new users (&quot;Referred Users&quot;) who sign up for a paid ActiveShape
            subscription. Commissions are earned for as long as both the Referrer and the Referred User maintain active
            accounts.
          </p>

          <h2 className={b('section-title')}>2. Eligibility</h2>
          <p>To participate as a Referrer, you must:</p>
          <ul>
            <li>Have an active ActiveShape account in good standing</li>
            <li>Be at least 18 years of age (or the age of legal majority in your jurisdiction)</li>
            <li>Provide accurate and truthful information during Stripe Connect onboarding for payout purposes</li>
            <li>Comply with all applicable laws and regulations, including tax obligations in your jurisdiction</li>
          </ul>
          <p>
            ActiveShape reserves the right to determine eligibility and may modify eligibility requirements at any time.
            A paid subscription is <strong>not</strong> required to participate as a Referrer.
          </p>

          <h2 className={b('section-title')}>3. How It Works</h2>

          <h3>3.1 Referral Code</h3>
          <p>
            Each Referrer receives a unique referral code and shareable link. You may optionally set a custom vanity
            slug for your referral link (subject to availability and content guidelines).
          </p>

          <h3>3.2 Attribution</h3>
          <p>
            When a new user visits ActiveShape through your referral link, your referral code is stored in their
            browser. If that user subsequently creates an account and subscribes to a paid plan, the referral is
            attributed to you. Attribution is based on a first-touch model — only the first referral code used by a new
            user is credited.
          </p>

          <h3>3.3 Qualification Period</h3>
          <p>
            A referral enters a <strong>30-day qualification period</strong> starting from the Referred User&apos;s
            signup date. Commission payments begin only after the Referred User has maintained an active paid
            subscription for at least 30 consecutive days.
          </p>

          <h2 className={b('section-title')}>4. Commission Structure</h2>
          <p>
            Commissions are calculated as a percentage of the Referred User&apos;s current monthly plan price,
            determined by the Referrer&apos;s number of active (qualified) referrals:
          </p>
          <ul>
            <li>
              <strong>1–9 active referrals:</strong> 10% commission per referred subscriber per month
            </li>
            <li>
              <strong>10–24 active referrals:</strong> 15% commission per referred subscriber per month
            </li>
            <li>
              <strong>25+ active referrals:</strong> 20% commission per referred subscriber per month
            </li>
          </ul>
          <p>
            Commission rates are <strong>dynamic</strong> — they are always calculated from the Referred User&apos;s
            current plan price at the time of each billing cycle. If a Referred User upgrades or downgrades their plan,
            the commission amount adjusts accordingly.
          </p>
          <p>
            ActiveShape reserves the right to modify the commission structure at any time. Changes will be communicated
            at least 30 days in advance and will apply to future commission periods only.
          </p>

          <h2 className={b('section-title')}>5. Payouts</h2>

          <h3>5.1 Payment Method</h3>
          <p>
            All payouts are processed through <strong>Stripe Connect Express</strong>. You must complete the Stripe
            Connect onboarding process (including identity verification and tax information) before you can receive
            payouts.
          </p>

          <h3>5.2 Minimum Payout</h3>
          <p>
            Payouts are issued only when your pending balance reaches a minimum of <strong>$50.00 USD</strong>. Balances
            below this threshold will carry over to the next payout period.
          </p>

          <h3>5.3 Currency</h3>
          <p>
            All commissions are calculated and paid in <strong>United States Dollars (USD)</strong>, regardless of the
            currency in which the Referred User pays for their subscription.
          </p>

          <h3>5.4 Payout Schedule</h3>
          <p>
            Payouts are processed on a monthly basis. The exact timing may vary depending on Stripe processing times and
            banking schedules. ActiveShape is not responsible for delays caused by third-party payment processors or
            financial institutions.
          </p>

          <h3>5.5 Tax Responsibility</h3>
          <p>
            You are solely responsible for reporting and paying all applicable taxes on commissions earned through the
            Program. Stripe Connect Express handles tax form generation (such as IRS Form 1099 for US-based
            participants) automatically. ActiveShape does not provide tax advice.
          </p>

          <h2 className={b('section-title')}>6. Commission Lifecycle</h2>
          <p>Commissions follow the lifecycle of the Referred User&apos;s subscription:</p>
          <ul>
            <li>
              <strong>Active:</strong> Commission is earned each billing cycle while the Referred User maintains an
              active paid subscription
            </li>
            <li>
              <strong>Paused:</strong> Commission is temporarily suspended if the Referred User&apos;s payment fails or
              their subscription enters a past-due state
            </li>
            <li>
              <strong>Resumed:</strong> Commission resumes automatically when the Referred User&apos;s payment is
              recovered
            </li>
            <li>
              <strong>Expired:</strong> Commission permanently ends if the Referred User cancels their subscription
            </li>
          </ul>

          <h2 className={b('section-title')}>7. Prohibited Activities</h2>
          <p>
            The following activities are strictly prohibited and may result in immediate termination from the Program,
            forfeiture of pending commissions, and/or suspension of your ActiveShape account:
          </p>
          <ul>
            <li>
              <strong>Self-referrals:</strong> Creating multiple accounts to refer yourself
            </li>
            <li>
              <strong>Fake referrals:</strong> Using automated bots, scripts, or fake identities to generate signups
            </li>
            <li>
              <strong>Shared payment methods:</strong> Using the same credit card, bank account, or payment instrument
              for both the Referrer&apos;s and Referred User&apos;s accounts
            </li>
            <li>
              <strong>Misleading promotion:</strong> Making false or misleading claims about ActiveShape, its features,
              pricing, or the referral program itself
            </li>
            <li>
              <strong>Spam:</strong> Sending unsolicited bulk emails, messages, or social media communications to
              promote your referral link
            </li>
            <li>
              <strong>Trademark misuse:</strong> Using ActiveShape trademarks, logos, or brand assets in a manner that
              implies official endorsement or partnership without prior written consent
            </li>
            <li>
              <strong>Incentive manipulation:</strong> Offering direct cash incentives, gift cards, or other
              consideration to Referred Users in exchange for signing up (beyond sharing the ActiveShape service itself)
            </li>
            <li>
              <strong>Disposable accounts:</strong> Using disposable email addresses or temporary phone numbers for
              referred signups
            </li>
          </ul>

          <h2 className={b('section-title')}>8. Fraud Detection and Enforcement</h2>
          <p>
            ActiveShape employs automated and manual fraud detection measures to protect the integrity of the Program.
            These may include, but are not limited to:
          </p>
          <ul>
            <li>IP address analysis for suspicious signup patterns</li>
            <li>Payment method fingerprint comparison between Referrer and Referred Users</li>
            <li>Email domain validation (disposable email services are flagged)</li>
            <li>Rate limiting on referral code validations</li>
            <li>Monthly referral volume monitoring</li>
          </ul>
          <p>
            Referrals flagged by our fraud detection system may be placed under manual review. ActiveShape reserves the
            sole right to determine whether a referral qualifies for commission and may withhold, reverse, or claw back
            commissions associated with fraudulent or suspicious activity.
          </p>

          <h2 className={b('section-title')}>9. FTC Disclosure Requirements</h2>
          <p>
            If you promote ActiveShape through blogs, social media, videos, or any public-facing content, you are
            required by the U.S. Federal Trade Commission (FTC) and similar regulatory bodies in other jurisdictions to
            clearly disclose your financial relationship with ActiveShape.
          </p>
          <p>Your disclosure must be:</p>
          <ul>
            <li>
              <strong>Clear and conspicuous:</strong> Easily noticeable and understandable by the average reader/viewer
            </li>
            <li>
              <strong>Close to the referral link:</strong> Placed near the referral link or recommendation, not buried
              in a separate page or footnote
            </li>
            <li>
              <strong>Honest:</strong> Accurately describe the nature of the relationship (e.g., &quot;I earn a
              commission if you sign up through my link&quot;)
            </li>
          </ul>

          <h2 className={b('section-title')}>10. Program Modifications and Termination</h2>

          <h3>10.1 Modifications</h3>
          <p>
            ActiveShape may modify, suspend, or discontinue the Program (or any part thereof) at any time. Material
            changes to commission rates, payout thresholds, or eligibility requirements will be communicated at least 30
            days in advance via email or in-app notification.
          </p>

          <h3>10.2 Individual Termination</h3>
          <p>
            ActiveShape may terminate your participation in the Program at any time, with or without cause. In cases of
            fraud or violation of these Referral Terms, termination may be immediate and without notice, and any pending
            commissions may be forfeited.
          </p>

          <h3>10.3 Voluntary Withdrawal</h3>
          <p>
            You may stop participating in the Program at any time by contacting us at{' '}
            <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>. Any pending commissions that have met the minimum
            payout threshold will be processed in the next payout cycle.
          </p>

          <h2 className={b('section-title')}>11. Limitation of Liability</h2>
          <p>
            ActiveShape&apos;s total liability to any Referrer under the Program shall not exceed the total commissions
            actually paid to that Referrer in the 12 months preceding the claim. ActiveShape shall not be liable for
            indirect, incidental, special, consequential, or punitive damages arising from participation in the Program.
          </p>

          <h2 className={b('section-title')}>12. Relationship of the Parties</h2>
          <p>
            Participation in the Program does not create an employment, partnership, joint venture, or agency
            relationship between you and ActiveShape. You are an independent participant and are not authorized to make
            commitments or representations on behalf of ActiveShape.
          </p>

          <h2 className={b('section-title')}>13. Data and Privacy</h2>
          <p>
            To operate the Program, ActiveShape collects and processes certain data, including your referral code usage,
            the number of Referred Users, commission amounts, and payout information. This data is processed in
            accordance with our <a href="/privacy-policy">Privacy Policy</a>. Referrers do not receive personally
            identifiable information about their Referred Users beyond anonymized initials and subscription status.
          </p>

          <h2 className={b('section-title')}>14. Contact</h2>
          <p>
            If you have questions about these Referral Program Terms or need assistance, contact us at:{' '}
            <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>
          </p>

          <div className={b('footer')}>
            <p>
              By participating in the ActiveShape Referral Program, you confirm that you have read, understood, and
              accepted these Referral Terms, as well as our general Terms of Service and Privacy Policy.
            </p>
            <div className={b('footer', 'links')}>
              <TextLinkColor to="/terms-of-service">Terms of Service</TextLinkColor>
              <TextLinkColor to="/privacy-policy">Privacy Policy</TextLinkColor>
              <TextLinkColor to="/user-data-deletion">Data Deletion Policy</TextLinkColor>
            </div>
          </div>
        </div>
      </div>
    </StyledReferralTerms>
  );
};

export default ReferralTermsPage;
